import { useState, useMemo, type ReactNode } from "react";

import { RVAppLayout, PageContext } from "@regal-voice/ui";
import { SpotlightSearchProvider } from "Components/elements/SpotlightSearch/SpotlightSearchContext";
import { AgentStatusDropdown } from "Components/shared/AgentStatusDropdown/AgentStatusDropdown";
import { DraggableIntercomButton } from "Components/shared/DraggableIntercomButton/DraggableIntercomButton";
import { getEmbedUiConfigByKey } from "Services/embed/CrossDomainMessenger";
import { isIframed } from "Services/embed/EmbedUtilService";
import { useFlags } from "Services/FeatureFlagService";

import { AppHeader } from "./Components/AppHeader/AppHeader";
import { AppNav } from "./Components/AppNav/AppNav";
import { SearchableDialPadContainer } from "./Components/SearchableDialPadContainer/SearchableDialPadContainer";

const APP_NAME = "Regal.ai";

type AppLayoutProps = {
    children: any;
    pageReady: boolean;
};

export function AppLayout({ children, pageReady }: AppLayoutProps): JSX.Element {
    const [showSidebar, setShowSidebar] = useState(true);
    const [pageTitle, setPageTitle] = useState<string | undefined>();
    const [headerActions, setHeaderActions] = useState<ReactNode>(null);

    const { customDraggableIntercomButton } = useFlags();

    const pageContextValue = useMemo(
        () => ({ setShowSidebar, setPageTitle, setHeaderActions }),
        [setShowSidebar, setPageTitle, setHeaderActions]
    );

    const shouldHideSidenavWhenEmbedded = getEmbedUiConfigByKey("hideNavigation");
    const shouldHideLogoWhenEmbedded = getEmbedUiConfigByKey("hideLogo");
    const isAppEmbedded = isIframed();
    return (
        <SpotlightSearchProvider>
            <PageContext.Provider value={pageContextValue}>
                <RVAppLayout
                    appTitle={APP_NAME}
                    showSidebar={showSidebar}
                    pageTitle={pageTitle}
                    shouldHideSidenavWhenEmbedded={shouldHideSidenavWhenEmbedded}
                    isAppEmbedded={isAppEmbedded}
                    sideNav={<AppNav />}
                    loading={!pageReady}
                    header={
                        <AppHeader
                            showSidebar={showSidebar}
                            shouldHideLogoWhenEmbedded={shouldHideLogoWhenEmbedded}
                            headerActions={headerActions}
                            agentStatusDropdown={<AgentStatusDropdown />}
                        />
                    }
                >
                    {children}
                </RVAppLayout>
                <SearchableDialPadContainer />
                {customDraggableIntercomButton && <DraggableIntercomButton />}
            </PageContext.Provider>
        </SpotlightSearchProvider>
    );
}
